import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, map, Observable, of, switchMap } from 'rxjs';
import * as ListOfficerSelectors from './list-officer-store.selectors';
import * as ListOfficerActions from './list-officer-store.actions';
import { IListOfficerState } from './list-officer-store.state';
import { Officer, OfficerForOrganizationChart } from '../models/list-officer.models';
import { TableData } from '@shared/ui/tables';
import { ActionType, ListOfficerActionResult } from '../models/list-officer-action-result.models';
import { SelectOption } from '@shared/ui/inputs';

@Injectable({
  providedIn: 'root',
})
export class ListOfficerFacade {
  public constructor(private readonly store: Store<IListOfficerState>) {}

  public listOfficer(): void {
    this.store.dispatch(ListOfficerActions.listOfficer());
  }

  public listPaginationOfficers(query: string): void {
    this.store.dispatch(ListOfficerActions.listPaginationOfficers({ query: query }));
  }

  public getOfficers(): Observable<TableData<Officer>> {
    return this.store.select(ListOfficerSelectors.officerState).pipe(map((x) => new TableData(x.officers.length, x.officers)));
  }

  public getPaginationOfficers(): Observable<TableData<Officer>> {
    return this.store.select(ListOfficerSelectors.officerState).pipe(map((x) => new TableData(x.filteredOfficersTotalCount, x.paginationOfficers)));
  }

  public getOfficersQuery(): Observable<string> {
    return this.store.select(ListOfficerSelectors.officerQuery);
  }

  public isLoadingOfficers(): Observable<boolean> {
    return this.store.select(ListOfficerSelectors.isLoadingOfficers);
  }

  public deleteOfficer(uid: string): void {
    this.store.dispatch(ListOfficerActions.deleteOfficer({ uid: uid }));
  }

  public listOfficerActionResult(actionType: ActionType): Observable<ListOfficerActionResult> {
    return this.isLoadingOfficers().pipe(
      switchMap((isLoadingOfficers) => {
        if (isLoadingOfficers) {
          return of();
        }

        return this.store.select(ListOfficerSelectors.actionResult).pipe(filter((x): x is ListOfficerActionResult => !!x && actionType === ActionType.DeleteOfficer));
      })
    );
  }

  public getOfficersSelectOption(appliedFilters: (officer: Officer) => boolean = () => true): Observable<SelectOption[]> {
    return this.getOfficers().pipe(
      map((x) => {
        const options: SelectOption[] = [];

        x.data
          .filter(appliedFilters)
          .sort((a, b) => (a.isDeleted === b.isDeleted ? 0 : a.isDeleted ? 1 : -1) || a.firstName.localeCompare(b.firstName))
          .map((officer) => options.push(new SelectOption(`${officer.firstName} ${officer.lastName} ${officer.isDeleted ? 'DELETED' : ''}`, officer.uid)));

        return options;
      })
    );
  }

  public listOfficerForOrganizationChart(): void {
    this.store.dispatch(ListOfficerActions.listOfficerForOrganizationChart());
  }

  public inviteUser(uid: string): void {
    this.store.dispatch(ListOfficerActions.inviteUser({ uid: uid }));
  }

  public isInvitingUser(uid: string): Observable<boolean> {
    return this.store.select(ListOfficerSelectors.isInvitingUser(uid));
  }

  public getOrgChartOfficers(): Observable<OfficerForOrganizationChart[]> {
    return this.store.select(ListOfficerSelectors.orgChartOfficers);
  }
}
